import { Can } from '@/domains/access-control';
import { useAuth } from '@/domains/auth';
import { PageHeader } from '@/shared/components/PageHeader';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Edit, KeyRound, Lock, TerminalSquare, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import { JobInformationSection } from '../sections/JobInformationSection';
import { LogSection } from '../sections/LogSection';
import { PersonalDetailsSection } from '../sections/PersonalDetailsSection';
import type { ColorScheme, SectionKey, User, UserDetail } from '../types/user';
import { UserTabNavigation } from './UserTabNavigation';
import { ChangePasswordDialog } from './dialogs/ChangePasswordDialog';
import { DeactivateUserDialog } from './dialogs/DeactivateUserDialog';
import { EditUserDetailsDialog } from './dialogs/EditUserDetailsDialog';
import { ReactivateUserDialog } from './dialogs/ReactivateUserDialog';
import { ResetPasswordDialog } from './dialogs/ResetPasswordDialog';
import { SetPasswordDialog } from './dialogs/SetPasswordDialog';

interface UserDetailViewProps {
  userId: string;
  userDetail: UserDetail;
  user: User;
  onBack: () => void;
}

const SECTION_KEYS: readonly SectionKey[] = ['personal', 'job', 'log'];

/** URL-backed view state: which tab is open and the log's current page. */
const DETAIL_VIEW_DEFAULTS: Record<string, string> = {
  tab: 'personal',
  page: '1',
};

export function UserDetailView({ userId, userDetail, user, onBack }: UserDetailViewProps) {
  const [filters, setFilters] = useUrlFilters(DETAIL_VIEW_DEFAULTS);
  const activeSection: SectionKey = SECTION_KEYS.includes(filters.tab as SectionKey)
    ? (filters.tab as SectionKey)
    : 'personal';
  const logPage = Math.max(1, parseInt(filters.page, 10) || 1);

  // Switching tabs resets the log page so a stale ?page= doesn't carry over.
  const setActiveSection = (section: SectionKey) => setFilters({ tab: section, page: '1' });
  const setLogPage = (page: number) => setFilters({ page: String(page) });

  const [colorScheme, _setColorScheme] = useState<ColorScheme>('neutral');
  const [editOpen, setEditOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [setPasswordDevOpen, setSetPasswordDevOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);

  const isActive = user.status !== 'offline';
  const hasCognitoAccount = Boolean(userDetail.cognitoUsername || userDetail.workEmail);
  const isDevBuild = import.meta.env.DEV;

  const { user: actor } = useAuth();

  // Any logged-in user may change their OWN password from their profile
  // (WRK-5238) — independent of the admin "update User" capability that gates
  // the actions below.
  const isOwnProfile = actor?.id != null && String(actor.id) === String(userId);

  // Authorisation to reset another user's password is the RBAC `update User`
  // capability (enforced by the <Can> wrapper below and the backend guard,
  // WRK-5474). We only additionally hide the button on the actor's OWN profile,
  // where the admin reset endpoint 400s (self-reset) and "Change Password" is
  // the correct affordance instead.
  const showResetButton = !isOwnProfile;

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      <PageHeader
        title={user.name}
        subtitle={`${user.role || '—'} • ${user.team || '—'}`}
        icon={
          <Avatar className="w-14 h-14 bg-orange-600 text-white border-2 border-white dark:border-gray-800">
            <AvatarFallback className="bg-orange-600 text-white">
              {getInitials(user.name)}
            </AvatarFallback>
          </Avatar>
        }
        actions={
          <div className="flex items-center gap-2">
            {isOwnProfile && (
              <Button
                variant="outline"
                className="border-border"
                onClick={() => setChangePasswordOpen(true)}
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            )}
            <Can action="update" subject="User">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="border-border"
                  onClick={() => setEditOpen(true)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit User Details
                </Button>
                {showResetButton && (
                  <Button
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => setResetPasswordOpen(true)}
                    disabled={!hasCognitoAccount}
                    title={hasCognitoAccount ? undefined : 'User has no Cognito account'}
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </Button>
                )}
                {isDevBuild && showResetButton && (
                  <Button
                    variant="outline"
                    className="border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-800 dark:text-yellow-400"
                    onClick={() => setSetPasswordDevOpen(true)}
                    disabled={!hasCognitoAccount}
                    title="Dev-only: sets password directly without email"
                  >
                    <TerminalSquare className="w-4 h-4 mr-2" />
                    Set Directly (dev)
                  </Button>
                )}
                {isActive ? (
                  <Button
                    variant="outline"
                    className="border-border text-red-600 hover:text-red-700 dark:text-red-400"
                    onClick={() => setDeactivateOpen(true)}
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="border-border text-green-600 hover:text-green-700 dark:text-green-400"
                    onClick={() => setReactivateOpen(true)}
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    Reactivate
                  </Button>
                )}
              </div>
            </Can>
          </div>
        }
      />

      <div className="px-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-2">
          ← Back to Users
        </Button>
      </div>

      <UserTabNavigation activeSection={activeSection} onSectionChange={setActiveSection} />

      <div className="flex-1 overflow-y-auto">
        <div>
          {activeSection === 'personal' && (
            <PersonalDetailsSection
              user={user}
              colorScheme={colorScheme}
              userId={userId}
              userDetail={userDetail}
            />
          )}
          {activeSection === 'job' && (
            <JobInformationSection
              user={user}
              colorScheme={colorScheme}
              userId={userId}
              userDetail={userDetail}
            />
          )}
          {activeSection === 'log' && (
            <LogSection user={user} userId={userId} page={logPage} onPageChange={setLogPage} />
          )}
        </div>
      </div>

      <EditUserDetailsDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        userId={userId}
        user={userDetail}
      />
      <ResetPasswordDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        userId={userId}
        userName={user.name}
      />
      {isOwnProfile && (
        <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      )}
      {isDevBuild && (
        <SetPasswordDialog
          open={setPasswordDevOpen}
          onOpenChange={setSetPasswordDevOpen}
          userId={userId}
          userName={user.name}
        />
      )}
      <DeactivateUserDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        userId={userId}
        userName={user.name}
      />
      <ReactivateUserDialog
        open={reactivateOpen}
        onOpenChange={setReactivateOpen}
        userId={userId}
        userName={user.name}
      />
    </div>
  );
}
